import type { MetadataFieldKey } from './metadataFields';

/**
 * The four kinds of asset, defined once — and the carousel master's rules.
 *
 * Mirrors `src/core/asset_types.py` in the Asset Library backend, which is the
 * authority: what a kind owns, what publishing requires of it, whether it is
 * cut from source images. Like `getMissingPublishFields` this is a copy of the
 * Python, kept so the form can show the right fields and the card can warn
 * before a click; anything that slips past it is skipped server-side and
 * reported from the response.
 */

export const ASSET_TYPE_KEYS = ['source_image', 'master_image', 'filmic', 'ola'] as const;
export type AssetType = (typeof ASSET_TYPE_KEYS)[number];

/**
 * The master image format whose stills are frames (`CAROUSEL_FORMAT`). Set by
 * the carousel upload, never picked: no master can be given it or lose it.
 */
export const CAROUSEL_FORMAT = 'carousel';

/**
 * Which set of rules a row is under: its kind's, or `master_carousel` for a
 * master image in the Carousel format (the backend's `MASTER_CAROUSEL`, picked
 * by `spec_of`). Forms, publish checks and the upload mode read rules; grouping
 * and filters read the kind.
 */
export const ASSET_RULE_KEYS = [
  'source_image',
  'master_image',
  'master_carousel',
  'filmic',
  'ola',
] as const;
export type AssetRules = (typeof ASSET_RULE_KEYS)[number];

/** What was uploaded: a still, a film, an HTML5 banner folder, or a carousel's stills. */
export type MediaKind = 'image' | 'video' | 'ola' | 'carousel';

/** `MIN_CAROUSEL_FRAMES` / `MAX_CAROUSEL_FRAMES` in the backend's `asset_types.py`. */
export const MIN_CAROUSEL_FRAMES = 2;
export const MAX_CAROUSEL_FRAMES = 6;
/** `MAX_CAROUSELS_PER_BATCH`: how many carousels one upload may open. */
export const MAX_CAROUSELS_PER_BATCH = 50;

/**
 * `FRAME_COPY_TYPES`: the copy a carousel frame carries. No CTA — on every
 * platform that runs a carousel the call to action is the platform's button.
 */
export const FRAME_COPY_TYPES = ['headline', 'subheadline', 'offer', 'disclaimer'] as const;

/**
 * The one publish requirement that is not a metadata field: width and height
 * both known. Read at confirm from the banner's `ad.size` meta.
 */
export const AD_SIZE_FIELD = 'ad_size' as const;

export interface AssetTypeSpec {
  key: AssetRules;
  label: string;
  /** The upload modal's radio: "Source images", "Filmic" … */
  pluralLabel: string;
  mediaKind: MediaKind;
  /** The metadata fields this kind carries; the form shows exactly these. */
  ownedFields: readonly MetadataFieldKey[];
  /**
   * What publishing requires on top of the fields every kind requires.
   * `AD_SIZE_FIELD` is a pseudo-field: width and height both known.
   */
  publishRequired: readonly (MetadataFieldKey | typeof AD_SIZE_FIELD)[];
  /** Cut from source images: a link is required to publish. False means a link is refused. */
  canHaveSources: boolean;
  /**
   * Made of frames, each naming its own source image. The asset's links are
   * derived from the frames server-side and cannot be edited directly.
   */
  hasFrames?: boolean;
}

const CORE_FIELDS = [
  'nameplate',
  'business_unit',
  'carline_code',
  'tier',
  'ai_indicator',
  // The licence and the domain: inherited by derivatives, and a film is
  // licensed by channel and market too.
  'channel_rights',
  'country_rights',
  'domain',
] as const satisfies readonly MetadataFieldKey[];

/**
 * Who shot it. Owned only by kinds that are not cut from a source (source
 * images and films): a master or a banner is the hero re-cut, so the agency
 * is the source image's to say.
 */
const AGENCY_FIELDS = ['source_agency'] as const satisfies readonly MetadataFieldKey[];

/**
 * Whether a hero is a lifestyle shot. Source images only, never inherited,
 * and never owed to publish: false is an answer, not a gap.
 */
const LIFESTYLE_FIELDS = ['is_lifestyle_image'] as const satisfies readonly MetadataFieldKey[];

/** Required of every kind. Nameplate, carline code and business unit are each owed in their own right. */
export const COMMON_PUBLISH_FIELDS = [
  'nameplate',
  'carline_code',
  'business_unit',
  'tier',
  'ai_indicator',
  'channel_rights',
  'country_rights',
  'domain',
] as const;

export const ASSET_TYPES: Record<AssetType, AssetTypeSpec> = {
  source_image: {
    key: 'source_image',
    label: 'Source image',
    pluralLabel: 'Source images',
    mediaKind: 'image',
    // The hero before any copy is laid over it, so no copy elements; licensed
    // by channel and market rather than by platform, so no platform rights.
    ownedFields: [...CORE_FIELDS, ...AGENCY_FIELDS, 'expiry_rights', ...LIFESTYLE_FIELDS],
    publishRequired: [...COMMON_PUBLISH_FIELDS, 'expiry_rights', 'source_agency'],
    canHaveSources: false,
  },
  master_image: {
    key: 'master_image',
    label: 'Master image',
    pluralLabel: 'Master images',
    mediaKind: 'image',
    // Cut for one ad format, which decides its channel and its briefing
    // match, and owed to publish. No platform rights: the format replaces them.
    // Copy is carried but optional: publishing does not wait on it.
    // No creative agency: that is its source images' to say.
    ownedFields: [...CORE_FIELDS, 'expiry_rights', 'copy_elements', 'format_type', 'format_size'],
    publishRequired: [...COMMON_PUBLISH_FIELDS, 'expiry_rights', 'format_type', 'format_size'],
    canHaveSources: true,
  },
  filmic: {
    key: 'filmic',
    label: 'Filmic',
    pluralLabel: 'Filmic',
    mediaKind: 'video',
    // One rights expiry, like a still; no platform rights. Its aspect ratio
    // (`format_size`, alone: no type, so no channel) and its length are
    // declared, never read from the video, and owed to publish.
    ownedFields: [
      ...CORE_FIELDS,
      ...AGENCY_FIELDS,
      'expiry_rights',
      'format_size',
      'duration_seconds',
      'legacy_fcv_id',
    ],
    publishRequired: [
      ...COMMON_PUBLISH_FIELDS,
      'expiry_rights',
      'format_size',
      'duration_seconds',
    ],
    // A film is not a re-cut of a still: there is nothing for it to be cut from.
    canHaveSources: false,
  },
  ola: {
    key: 'ola',
    label: 'OLA banner',
    pluralLabel: 'OLA banners',
    mediaKind: 'ola',
    // Runs on platforms and expires; the copy is baked into the HTML, so no
    // copy elements. No creative agency, as for a master.
    ownedFields: [
      ...CORE_FIELDS,
      'expiry_rights',
      'platform_rights',
      'campaign_name',
      'creative_variation',
      'production_ticket_ref',
      'briefing_form_ref',
      'notes',
    ],
    publishRequired: [
      ...COMMON_PUBLISH_FIELDS,
      'expiry_rights',
      'platform_rights',
      'campaign_name',
      AD_SIZE_FIELD,
    ],
    canHaveSources: true,
  },
};

/**
 * A master image in the Carousel format. Copy and source images are per frame,
 * and the format is read from the frames' pixels, so none of those are fields
 * to edit; it owes the format to publish all the same.
 */
export const MASTER_CAROUSEL: AssetTypeSpec = {
  key: 'master_carousel',
  label: 'Carousel',
  pluralLabel: 'Carousels',
  mediaKind: 'carousel',
  ownedFields: [...CORE_FIELDS, 'expiry_rights'],
  publishRequired: [...COMMON_PUBLISH_FIELDS, 'expiry_rights', 'format_type', 'format_size'],
  canHaveSources: true,
  hasFrames: true,
};

export const ASSET_RULES: Record<AssetRules, AssetTypeSpec> = {
  ...ASSET_TYPES,
  master_carousel: MASTER_CAROUSEL,
};

/** The rules one row is under. */
export function rulesOf(asset: { asset_type: AssetType; format_type?: string | null }): AssetRules {
  return asset.asset_type === 'master_image' && asset.format_type === CAROUSEL_FORMAT
    ? 'master_carousel'
    : asset.asset_type;
}

export function specOf(asset: { asset_type: AssetType; format_type?: string | null }) {
  return ASSET_RULES[rulesOf(asset)];
}

/** The distinct rules in a selection, in display order: what its form can show. */
export function assetRulesOf(
  assets: readonly { asset_type: AssetType; format_type?: string | null }[],
): AssetRules[] {
  const present = new Set(assets.map(rulesOf));
  return ASSET_RULE_KEYS.filter((key) => present.has(key));
}

export function isAssetType(value: unknown): value is AssetType {
  return typeof value === 'string' && (ASSET_TYPE_KEYS as readonly string[]).includes(value);
}

export function mediaKindOf(assetType: AssetType): MediaKind {
  return ASSET_TYPES[assetType].mediaKind;
}

export function assetTypeLabel(assetType: string | null | undefined): string {
  return isAssetType(assetType) ? ASSET_TYPES[assetType].label : (assetType ?? '');
}

/** The distinct kinds in a selection, in display order. */
export function assetTypesOf(assets: readonly { asset_type: AssetType }[]): AssetType[] {
  const present = new Set(assets.map((asset) => asset.asset_type));
  return ASSET_TYPE_KEYS.filter((key) => present.has(key));
}

/**
 * The fields every one of the given kinds carries — what a mixed selection
 * can edit in bulk. Empty input yields the empty set: a form for nothing shows
 * nothing.
 */
export function intersectOwnedFields(types: readonly AssetRules[]): Set<MetadataFieldKey> {
  if (types.length === 0) return new Set();
  const [first, ...rest] = types;
  return new Set(
    ASSET_RULES[first].ownedFields.filter((field) =>
      rest.every((type) => ASSET_RULES[type].ownedFields.includes(field)),
    ),
  );
}

/** Whether publishing requires `field` of every one of the given kinds. */
export function isPublishRequired(
  types: readonly AssetRules[],
  field: MetadataFieldKey | typeof AD_SIZE_FIELD,
): boolean {
  return (
    types.length > 0 && types.every((type) => ASSET_RULES[type].publishRequired.includes(field))
  );
}
